/* __next_internal_client_entry_do_not_use__ DIMENSION_BY_SIZE,optimizeAvatar,Avatar auto */ import { _ as _sliced_to_array } from "@swc/helpers/_/_sliced_to_array";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Core } from "@faire/design-tokens";
import { extractImageCropFromTag } from "@faire/web--source/common/images/cropUtils/extractImageCropFromTag";
import { optimizeImageUrl, optimizeImageUrlFillSquare } from "@faire/web--source/common/images/optimizeImages";
import { useStrictLocalization } from "@faire/web--source/common/localization";
import { isNotUndefined } from "@faire/web--source/common/typescriptUtils";
import { Row } from "@faire/web--source/slate/Layout";
import { Typography } from "@faire/web--source/slate/Typography";
import { useResponsiveValue } from "@faire/web--source/ui/hooks/useResponsiveValue";
import { TransparencyOverlay } from "@faire/web--source/ui/TransparencyOverlay";
import React from "react";
import styled, { css, useTheme } from "styled-components";
import { useIsSSR } from "../../common/server-side-rendering/isSSR";
import { SVGIcon } from "../icons/SVGIcon";
import { themeOrDefault } from "../Theme";
import { FaireMarketTheme } from "../Theme/EventsTheme";
import { buildResponsiveCss } from "../Theme/ResponsiveCss";
import { isResponsiveProp } from "../Theme/ResponsiveProp";
import { highVisSlateComponent, HIGH_VIS_FILTER } from "../utils/highVisSlateComponent";
var AvatarSizeToTypographyVariant = {
    "x-large": "displaySSansSemiBold",
    large: "sectionHeaderSansMedium",
    medium: "sectionHeaderSansMedium",
    small: "paragraphSansMedium",
    smaller: "paragraphSansMedium",
    "x-small": "paragraphSansMedium",
    "xx-small": "labelSansMedium"
};
var DEGREE_BY_POSITION = {
    right: 0,
    topRight: 45,
    top: 90,
    topLeft: 135,
    left: 180,
    bottomLeft: 225,
    bottom: 270,
    bottomRight: 315
};
export var DIMENSION_BY_SIZE = {
    "xx-small": 24,
    "x-small": 32,
    smaller: 40,
    small: 48,
    medium: 56,
    large: 64,
    "x-large": 96
};
export var optimizeAvatar = function(image, size, isSSR) {
    var dimension = DIMENSION_BY_SIZE[size];
    if (extractImageCropFromTag(image.tags)) {
        return optimizeImageUrl(image, isSSR, {
            width: dimension,
            height: dimension
        });
    }
    return optimizeImageUrlFillSquare(image, isSSR, dimension);
};
/**
 * @link [Docs](https://slate.faire.team/slate/component/avatar)
 * @trackcomponent
 */ export var Avatar = function(param) {
    var size = param.size, image = param.image, userName = param.userName, userCompany = param.userCompany, iconData = param.iconData, className = param.className, borderColor = param.borderColor, borderStyle = param.borderStyle, showEventIcon = param.showEventIcon, hideTransparencyOverlay = param.hideTransparencyOverlay, altTag = param.altTag;
    var strictLocalize = useStrictLocalization().strictLocalize;
    var theme = useTheme();
    var responsiveSizeValue = useResponsiveValue(isResponsiveProp(size) ? size : {
        mobileAndAbove: size
    });
    var _React_useState = _sliced_to_array(React.useState(false), 2), imageError = _React_useState[0], setImageError = _React_useState[1];
    var isSSR = useIsSSR();
    var onImageError = function() {
        if (!imageError) {
            setImageError(true);
        }
    };
    var owner = [
        userName,
        userCompany
    ].filter(isNotUndefined).join(", ");
    var alt;
    if (altTag) {
        // altTag overrides all available alts
        alt = altTag;
    } else if (image === null || image === void 0 ? void 0 : image.alt_text) {
        alt = image.alt_text;
    } else if (owner) {
        alt = strictLocalize({
            id: "ohjOwc",
            defaultMessage: [
                {
                    type: 1,
                    value: "PersonOrCompanyName"
                },
                {
                    type: 0,
                    value: "'s avatar"
                }
            ]
        }, {
            PersonOrCompanyName: owner
        });
    } else {
        alt = strictLocalize({
            id: "B05NZ9",
            defaultMessage: [
                {
                    type: 0,
                    value: "Avatar"
                }
            ]
        });
    }
    var _theme_avatar_placeholder_color, _userName_, _ref;
    return /*#__PURE__*/ _jsxs(Container, {
        children: [
            image && !imageError ? /*#__PURE__*/ _jsx(AvatarWrapper, {
                borderColor: borderColor,
                borderStyle: borderStyle,
                children: /*#__PURE__*/ _jsxs(OverlayContainer, {
                    children: [
                        /*#__PURE__*/ _jsx(Image, {
                            className: className,
                            alt: alt,
                            title: owner,
                            $size: size,
                            src: optimizeAvatar(image, responsiveSizeValue, isSSR),
                            onError: onImageError
                        }),
                        hideTransparencyOverlay ? null : /*#__PURE__*/ _jsx(TransparencyOverlay, {})
                    ]
                })
            }) : /*#__PURE__*/ _jsx(AvatarWrapper, {
                borderColor: borderColor,
                borderStyle: borderStyle,
                children: /*#__PURE__*/ _jsx(Placeholder, {
                    $size: size,
                    title: owner,
                    className: className,
                    children: /*#__PURE__*/ _jsx(Typography, {
                        color: (_theme_avatar_placeholder_color = theme.avatar.placeholder.color) !== null && _theme_avatar_placeholder_color !== void 0 ? _theme_avatar_placeholder_color : Core.text.default,
                        variant: AvatarSizeToTypographyVariant[responsiveSizeValue],
                        children: ((_ref = (_userName_ = userName === null || userName === void 0 ? void 0 : userName[0]) !== null && _userName_ !== void 0 ? _userName_ : userCompany === null || userCompany === void 0 ? void 0 : userCompany[0]) !== null && _ref !== void 0 ? _ref : "").toUpperCase()
                    })
                })
            }),
            iconData ? /*#__PURE__*/ _jsx(IconContainer, {
                $degree: DEGREE_BY_POSITION[iconData.position],
                children: iconData.icon
            }) : null,
            showEventIcon ? /*#__PURE__*/ _jsx(IconContainer, {
                $degree: DEGREE_BY_POSITION.topRight,
                children: renderEventsIcon(theme, !!borderColor)
            }) : null
        ]
    });
};
var AvatarWrapper = function(param) {
    var borderColor = param.borderColor, _param_borderStyle = param.borderStyle, borderStyle = _param_borderStyle === void 0 ? "OUTLINED" : _param_borderStyle, children = param.children;
    return borderColor ? /*#__PURE__*/ _jsx(ContainerWithBorder, {
        $borderColor: borderColor,
        $borderStyle: borderStyle,
        children: children
    }) : /*#__PURE__*/ _jsx(_Fragment, {
        children: children
    });
};
var avatarStyles = function(size, styledComponentsTheme, isPlaceholder) {
    var _theme_avatar, _theme_avatar1;
    var theme = themeOrDefault(styledComponentsTheme);
    var _theme_avatar_placeholder_background, _theme_avatar_background;
    var backgroundColor = isPlaceholder ? (_theme_avatar_placeholder_background = (_theme_avatar = theme.avatar) === null || _theme_avatar === void 0 ? void 0 : _theme_avatar.placeholder.background) !== null && _theme_avatar_placeholder_background !== void 0 ? _theme_avatar_placeholder_background : Core.surface.default : (_theme_avatar_background = (_theme_avatar1 = theme.avatar) === null || _theme_avatar1 === void 0 ? void 0 : _theme_avatar1.background) !== null && _theme_avatar_background !== void 0 ? _theme_avatar_background : Core.surface.inverse;
    var sizeBasedStyles = function(size) {
        return "".concat(DIMENSION_BY_SIZE[size], "px");
    };
    return css([
        "border-radius:50%;flex-shrink:0;background-color:",
        ";max-width:none;",
        " ",
        ""
    ], backgroundColor, buildResponsiveCss("width", size, sizeBasedStyles), buildResponsiveCss("height", size, sizeBasedStyles));
};
var Image = styled.img.withConfig({
    componentId: "sc-2a3d4533-0"
})([
    "",
    ";"
], function(param) {
    var $size = param.$size, theme = param.theme;
    return avatarStyles($size, theme);
});
var Placeholder = styled(Row).attrs({
    align: "center",
    justify: "center"
}).withConfig({
    componentId: "sc-2a3d4533-1"
})([
    "",
    ";"
], function(param) {
    var size = param.$size, theme = param.theme;
    return avatarStyles(size, theme, true);
});
var degreeToRadian = function(degrees) {
    return degrees * (Math.PI / 180);
};
var Container = styled(Row).withConfig({
    componentId: "sc-2a3d4533-2"
})([
    "position:relative;width:fit-content;height:fit-content;flex-shrink:0;",
    ""
], highVisSlateComponent(HIGH_VIS_FILTER.COMPONENT));
var IconContainer = styled.div.withConfig({
    componentId: "sc-2a3d4533-3"
})([
    "position:absolute;",
    " transform:translate(50%,-50%);"
], function(param) {
    var $degree = param.$degree;
    return css([
        "right:",
        "%;top:",
        "%;"
    ], 50 * (1 - Math.cos(degreeToRadian($degree))), 50 * (1 - Math.sin(degreeToRadian($degree))));
});
var renderEventsIcon = function(theme, hasBorder) {
    if (!(theme === null || theme === void 0 ? void 0 : theme.eventIcon)) {
        return null;
    }
    var Component = theme.eventIcon.Component;
    return /*#__PURE__*/ _jsxs(Wrapper, {
        align: "center",
        $hasBorder: !!hasBorder,
        children: [
            /*#__PURE__*/ _jsx(DynamicIcon, {
                as: Component,
                width: theme.name === FaireMarketTheme.name ? "14px" : "12px",
                height: theme.name === FaireMarketTheme.name ? "14px" : "12px",
                $left: theme.name === FaireMarketTheme.name ? "3px" : "4px"
            }),
            /*#__PURE__*/ _jsx(InnerBorder, {})
        ]
    });
};
var ContainerWithBorder = styled(Row).withConfig({
    componentId: "sc-2a3d4533-4"
})([
    "",
    " background-color:",
    ";border-radius:50%;"
], function(param) {
    var $borderColor = param.$borderColor, $borderStyle = param.$borderStyle;
    return getBorderStyles($borderColor, $borderStyle);
}, Core.surface.inverse);
var getBorderStyles = function(borderColor, borderStyle) {
    if (borderStyle === "THIN") {
        return css([
            "border:1px solid ",
            ";"
        ], borderColor);
    } else if (borderStyle === "OUTLINED") {
        return css([
            "border:2px solid ",
            ";padding:1px;"
        ], borderColor);
    }
    return css([
        ""
    ]);
};
var OverlayContainer = styled(Row).withConfig({
    componentId: "sc-2a3d4533-5"
})([
    "position:relative;border-radius:50%;overflow:hidden;"
]);
var DynamicIcon = styled(SVGIcon).withConfig({
    componentId: "sc-2a3d4533-6"
})([
    "position:absolute;left:",
    ";"
], function(props) {
    return props.$left;
});
var Wrapper = styled(Row).withConfig({
    componentId: "sc-2a3d4533-7"
})([
    "width:20px;height:20px;border-radius:50%;background:",
    ";position:absolute;top:-8px;right:",
    ";"
], function(props) {
    return props.$backgroundColor;
}, function(props) {
    return props.$hasBorder ? "-10px" : "-13px";
});
var InnerBorder = styled.div.withConfig({
    componentId: "sc-2a3d4533-8"
})([
    "box-shadow:inset 0px 0px 0px 1px ",
    ";border-radius:50%;position:absolute;width:100%;height:inherit;"
], Core.surface.subdued);
